import * as React from 'react';
import cx from 'clsx';

import { QuicksilverTileOutfitType } from '../../game-tile';

import ClosedOverlay from '../ClosedOverlay';
import ModularGridGameTile from '../ModularGridGameTile';
import {
  GameTileProps,
  GameClickProps,
  OverlayProps,
  GridSizeType,
} from '../types';
import { CallbackMode } from '../../../logic/main/types';

import Banner from './Banner';
import styles from './GameBanner.module.scss';
import { selectProperStyles, prepareGameList } from '../utils';
import { MEDIUM_OUTFIT } from '../constants';

interface GameBannerProps {
  gameList?: GameTileProps[];
  onClick: (args: { gamePosition: number; game: GameClickProps }) => void;
  onInfoClick: (args: {
    gamePosition: number;
    game: GameClickProps;
    path: string;
  }) => void;
  insideHeader?: boolean;
  overlay?: OverlayProps;
  gameDetailsInfo: boolean;
  callbackMode: CallbackMode;
  size?: GridSizeType;
  gamesLimit?: number;
  initialIndex?: number;
}

const GameBanner: React.FC<GameBannerProps> = ({
  onClick,
  onInfoClick,
  insideHeader = true,
  overlay,
  gameDetailsInfo,
  callbackMode,
  gameList,
  gamesLimit = 7,
  size,
  initialIndex = 0,
}) => {
  if (!gameList && !overlay) return null;

  const createSlot = (
    gamePosition: number,
    outfit: QuicksilverTileOutfitType,
    slot?: GameTileProps
  ) => (
    <div
      className={cx(styles.slot, {
        [styles.rowSmallForced]: size === 'small',
        [styles.rowSmall]: size === undefined,
      })}
      key={`${gamePosition}-key`}
      data-testid={`${gamePosition}-slot`}
    >
      <ModularGridGameTile
        data-testid="modular-grid-tile"
        game={slot}
        onClick={handleClick(gamePosition, onClick)}
        onInfoClick={handleClick(gamePosition, onInfoClick)}
        outfit={outfit}
        isAnchor
        ghost={!!overlay}
        gameDetailsInfo={gameDetailsInfo}
        callbackMode={callbackMode}
      />
    </div>
  );
  const preparedGameList = prepareGameList(gameList, gamesLimit);
  const bannerSlot = preparedGameList[0];

  const handleClick = (gamePosition: number, fn: Function) => (
    game: GameClickProps,
    path?: string
  ) => {
    fn({ game, gamePosition, path });
  };

  return (
    <div
      className={cx(
        styles.container,
        insideHeader && styles.insideHeader,
        selectProperStyles(
          {
            small: styles.containerSmallForced,
            medium: styles.containerMediumForced,
            default: [styles.containerSmall, styles.containerMedium],
          },
          size
        )
      )}
      data-testid="game-banner-grid"
    >
      <div
        className={cx({
          [styles.bannerMediumForced]: size === 'medium',
          [styles.bannerMedium]: size === undefined,
        })}
      >
        <Banner
          topSlot={bannerSlot}
          onClick={() =>
            onClick({
              gamePosition: initialIndex + 1,
              game: bannerSlot,
            })
          }
          isAnchor
          onInfoClick={() =>
            onInfoClick({
              gamePosition: initialIndex + 1,
              game: bannerSlot,
              path: bannerSlot.gameDetailsPath,
            })
          }
          ghost={!!overlay}
          gameDetailsInfo={gameDetailsInfo}
          callbackMode={callbackMode}
        />
      </div>
      <div
        className={cx(
          selectProperStyles(
            {
              small: styles.rowSmallForced,
              medium: styles.rowMediumForced,
              default: [styles.rowSmall, styles.rowMedium],
            },
            size
          ),
          styles.row
        )}
      >
        {preparedGameList &&
          preparedGameList
            .slice(1, preparedGameList.length)
            .map((g, index) =>
              createSlot(index + initialIndex + 2, MEDIUM_OUTFIT, g)
            )}
      </div>
      {!!overlay && <ClosedOverlay {...overlay} />}
    </div>
  );
};
export default GameBanner;
